import { useEffect, useRef } from "react"
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom"

import useRedirectOnBeIdChange from '../../../hooks/useRedirectOnBeIdChange.js'
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Row from 'react-bootstrap/Row'

import AdministrationTabs from '../components/AdministrationTabs.jsx'
import DebugAccordion from "../../../components/DebugAccordion.jsx"
import Loadable from "../../../components/Loadable.jsx"
import LoadingAnimation from "../../../components/LoadingAnimation.jsx"
import PageTitle from "../../../components/PageTitle.jsx"
import TimestampWithUTCTooltip from '../../../components/TimestampWithUTCTooltip.jsx'

import { NotificationTypes, addNotification, notifyException } from "../../../state/actions/notificationActions.js"

import { fetchRole } from "../../../state/actions/roleActions.js"

import { userSignal, userLoadedSignal, userErrorSignal } from "../../../state/signals/userSignals.js"
import { fetchUser, updateUser, validateUsername, validateEmail, fetchUserRoles, fetchUserGroups } from "../../../state/actions/userActions.js"

export default function EditUserProfile() {

    const navigate = useNavigate()
    const { beId, userId } = useParams()
    useRedirectOnBeIdChange(beId, `/be/${beId}/management/users`)
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData } = useOutletContext()

    const usernameSignal = useSignal(null)
    const firstNameSignal = useSignal(null)
    const lastNameSignal = useSignal(null)
    const emailAddressSignal = useSignal(null)


    const canEditComputed = useComputed(
        () => {
            return (userPermissionsSummary?.value?.canEditUsers == true)
        }
    )

    useEffect(() => {
        fetchUser(userId, config, accessToken).then(
            (user) => {
                usernameSignal.value = user.username
                firstNameSignal.value = user.firstName
                lastNameSignal.value = user.lastName
                emailAddressSignal.value = user.email
            }
        )
        fetchUserRoles(userId, config, accessToken).then(
            (roleBusinessEntities) => {
                //console.log(roleBusinessEntities)
                roleBusinessEntities.map((role) => {
                    fetchRole(role.roleId, config, accessToken)
                })

            }

        )
    }, [userId, config, accessToken, usernameSignal, firstNameSignal, lastNameSignal, emailAddressSignal])

    const pageTabSignal = useSignal("profile")

    const getActiveUserStatus = () => {
        if (!userLoadedSignal.value) {
            return <LoadingAnimation />
        }
        return userSignal.value && userSignal.value.isActive !== undefined
            ? (userSignal.value.isActive ? "Yes" : "No")
            : "Unknown"
    }

    const getLockedUserStatus = () => {
        if (!userLoadedSignal.value) {
            return <LoadingAnimation />
        }
        return userSignal.value && userSignal.value.isLocked !== undefined
            ? (userSignal.value.isLocked ? "Yes" : "No")
            : "Unknown"
    }

    const getInitialUserStatus = () => {
        if (!userLoadedSignal.value) {
            return <LoadingAnimation />
        }
        // flip status meaning for has logged in display field
        return userSignal.value && userSignal.value.isInitialUser !== undefined
            ? (!userSignal.value.isInitialUser ? "Yes" : "No")
            : "Unknown"
    }

    const getEmailVerifiedUserStatus = () => {
        if (!userLoadedSignal.value) {
            return <LoadingAnimation />
        }
        return userSignal.value && userSignal.value.emailAddressVerified !== undefined
            ? (userSignal.value.emailAddressVerified ? "Yes" : "No")
            : "Unknown"
    }

    const handleSubmitUpdateUser = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        userLoadedSignal.value = false
        try {
            let clonedData = structuredClone(userSignal.value)
            clonedData.username = usernameSignal.value
            clonedData.firstName = firstNameSignal.value
            clonedData.lastName = lastNameSignal.value
            clonedData.email = emailAddressSignal.value
            const [usernameResult, emailResult] = await Promise.all([
                validateUsername(userId, clonedData.username, config, accessToken),
                validateEmail(userId, clonedData.email, config, accessToken),
            ])

            let isAvailable = true
            if (!usernameResult?.isAvailable) {
                addNotification(`Username ${clonedData.username} is already in use`, NotificationTypes.ERROR, false, "Error")
                isAvailable = false
            }
            if (!emailResult?.isAvailable) {
                addNotification(`Email ${clonedData.email} is already in use`, NotificationTypes.ERROR, false, "Error")
                isAvailable = false
            }
            if (!isAvailable) return

            clonedData.lastUpdateDateTime = new Date().toISOString().replace('Z', '')
            updateUser(userId, clonedData, config, accessToken).then(
                () => {
                    addNotification("User was updated successfully", NotificationTypes.SUCCESS, false)
                    fetchUser(userId, config, accessToken)
                }
            )

        } catch (err) {
            addNotification("Unable to validate or update user", NotificationTypes.ERROR, false, "Error")
            console.error(err)
        } finally {
            userLoadedSignal.value = true
        }
    }

    const handleEditUserName = (e) => {
        usernameSignal.value = e.target.value
        const userNameRegex = new RegExp('^[A-Za-z0-9._@\\-]{1,50}$')
        if (userNameRegex.test(e.target.value)) {
            e.target.setCustomValidity("")
        }
        else {
            e.target.setCustomValidity("Must be between 1 and 50 valid characters: A-Z, a-z, 0-9, period (.), underscore (_), hyphen (-), or at-sign(@)")
        }
    }

    const handleEditFirstName = (e) => {
        firstNameSignal.value = e.target.value
        const firstNameRegex = new RegExp(`^[0-9A-Za-z .'\\-]{1,50}$`)
        if (firstNameRegex.test(e.target.value)) {
            e.target.setCustomValidity("")
        }
        else {
            e.target.setCustomValidity("Must be between 1 and 50 valid characters: 0-9, A-Z, a-z, period (.), hyphen (-), space ( ), or apostrophe (')")
        }
    }

    const handleEditLastName = (e) => {
        lastNameSignal.value = e.target.value
        const lastNameRegex = new RegExp(`^[A-Za-z .'\\-]{1,50}$`)
        if (lastNameRegex.test(e.target.value)) {
            e.target.setCustomValidity("")
        }
        else {
            e.target.setCustomValidity("Must be between 1 and 50 valid characters: A-Z, a-z, period (.), hyphen (-), space ( ), or apostrophe (')")
        }
    }

    const handleEditEmail = (e) => {
        emailAddressSignal.value = e.target.value
        const emailRegex = new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i)
        if (emailRegex.test(e.target.value)) {
            e.target.setCustomValidity("")
        }
        else {
            e.target.setCustomValidity("Must be a valid email address i.e. sample@email.com")
        }
    }

    const handleChangeUserStatus = () => {
        if (!userSignal.value) return;
        let isUserLocked = userSignal.value.isLocked;
        let isUserActive = userSignal.value.isActive;
        let clone = structuredClone(userSignal.value);
        if (isUserLocked) {
            clone.isLocked = false
        }
        if (isUserActive) {
            clone.isActive = false
        } else {
            clone.isActive = true
        }
        clone.lastUpdateDateTime = new Date().toISOString().replace('Z', '')
        userLoadedSignal.value = false
        updateUser(userId, clone, config, accessToken).then(
            () => {
                addNotification('User status was updated successfully', NotificationTypes.SUCCESS, false)
                fetchUser(userId, config, accessToken)
                userLoadedSignal.value = true
            }
        ).catch(
            () => {
                addNotification(`Unable to change user status`, NotificationTypes.ERROR, false, 'User Status Change Fail')
            }
        )
    }

    const resetForm = () => {
        document.getElementById("userName").value = userSignal.value.username
        document.getElementById("fname").value = userSignal.value.firstName
        document.getElementById("lname").value = userSignal.value.lastName
        document.getElementById("email").value = userSignal.value.email
    }

    return (
        <>
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/users` }}>Users</Breadcrumb.Item>
                <Breadcrumb.Item active>{userSignal.value ? userSignal.value.username : "Loading..."}</Breadcrumb.Item>
                <Breadcrumb.Item active>Profile</Breadcrumb.Item>
            </Breadcrumb>
            <PageTitle title='User Profile' canEditSignal={canEditComputed} />
            <AdministrationTabs activeKey="4" />
            <Nav
                variant="tabs"
                size="sm"
                className="px-3 my-3"
                activeKey={pageTabSignal.value}
            >
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}`} eventkey="profile" className="nav-link active">Profile</Link>
                </Nav.Item>
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}/roles`} eventkey="roles" className="nav-link">Roles</Link>
                </Nav.Item>
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}/permissions`} eventkey="permissions" className="nav-link">Permissions</Link>
                </Nav.Item>
            </Nav>

            {(pageTabSignal.value === "profile") && (
                <>
                    <div id='form-container'>
                        <Row className="">
                            <Col lg={6} className="border-end">
                                <Form validated onSubmit={handleSubmitUpdateUser} >
                                    <Row className="m-2 d-flex justify-content-center">
                                        <Form.Group as={Row}>
                                            <div className='col-md-8'>
                                                <Form.Label htmlFor='userName' column sm={4} className='control-label'>Username</Form.Label>
                                                <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                                                    <Form.Control
                                                        type='text'
                                                        sm={8}
                                                        disabled={!canEditComputed.value}
                                                        onChange={handleEditUserName}
                                                        className="form-control username"
                                                        id='userName'
                                                        name='userName'
                                                        maxLength='50'
                                                        required={true}
                                                        defaultValue={userSignal.value?.username}
                                                    />
                                                </Loadable>
                                                <Form.Control.Feedback type="invalid">Required. Username must be between 1 and 50 valid characters: A-Z, a-z, 0-9, period (.), underscore (_), hyphen (-), or at-sign(@)</Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                        <Form.Group as={Row}>
                                            <div className='col-md-8'>
                                                <Form.Label htmlFor='fname' sm={4} className='control-label'>First Name</Form.Label>
                                                <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                                                    <Form.Control type='text' sm={8} disabled={!canEditComputed.value} onChange={handleEditFirstName} className="form-control person" id='fname' name='fname' maxLength='50' required={true} defaultValue={userSignal.value?.firstName} />
                                                </Loadable>
                                                <Form.Control.Feedback type="invalid">Required. First Name must be between 1 and 50 valid characters: 0-9, A-Z, a-z, period (.), hyphen (-), space ( ), or apostrophe (&apos;)</Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                        <Form.Group as={Row}>
                                            <div className='col-md-8'>
                                                <Form.Label htmlFor='lname' sm={4} className='control-label'>Last Name</Form.Label>
                                                <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                                                    <Form.Control type='text' sm={8} disabled={!canEditComputed.value} onChange={handleEditLastName} className="form-control person" id='lname' name='lname' maxLength='50' required={true} defaultValue={userSignal.value?.lastName} />
                                                </Loadable>
                                                <Form.Control.Feedback type="invalid">Required. Last Name must be between 1 and 50 valid characters: A-Z, a-z, period (.), hyphen (-), space ( ), or apostrophe (&apos;)</Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                        <Form.Group as={Row}>
                                            <div className='col-md-8'>
                                                <Form.Label htmlFor='newEmail' sm={4} className='control-label'>Email Address</Form.Label>
                                                <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                                                    <Form.Control type='email' sm={8} disabled={!canEditComputed.value} onChange={handleEditEmail} placeholder='sample@email.com' className="form-control email" id='email' name='email' maxLength='200' required={true} defaultValue={userSignal.value?.email} />
                                                </Loadable>
                                                <Form.Control.Feedback type="invalid">Required. Must be a valid email address i.e. sample@email.com</Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                    </Row>
                                    <Row className="m-3 g-2">
                                        {canEditComputed.value && (<>
                                            <Col xs="auto">
                                                <Button type="submit">Apply</Button>
                                            </Col>
                                            <Col xs="auto">
                                                <Button variant="secondary" onClick={resetForm}>Cancel</Button>
                                            </Col>
                                        </>)}
                                    </Row>
                                </Form>
                            </Col>
                            <Col lg={4}>
                                <Row className="m-3">
                                    <div className="m-2">
                                        <span>Last Updated:&nbsp;</span>
                                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                                            <TimestampWithUTCTooltip>{userSignal.value?.lastUpdateDateTime}</TimestampWithUTCTooltip>
                                        </Loadable>
                                    </div>
                                    <div className="m-2">Active: {getActiveUserStatus()}</div>
                                    <div className="m-2">Account Locked: {getLockedUserStatus()}</div>
                                    <div className="m-2">Email Verified: {getEmailVerifiedUserStatus()}</div>
                                    <div className="m-2">Has Logged In: {getInitialUserStatus()}</div>
                                    {
                                        canEditComputed.value && (
                                            <Col xs="auto">
                                                <Button className="m-2 btn-warning" onClick={handleChangeUserStatus}>
                                                    <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                                                        {
                                                            userSignal.value && userSignal.value.isActive !== undefined
                                                                ? (userSignal.value.isActive ? "Disable" : "Enable")
                                                                : "Enable"
                                                        }
                                                    </Loadable>
                                                </Button>
                                            </Col>
                                        )
                                    }
                                </Row>
                            </Col>
                        </Row >
                    </div>
                </>
            )}
            <DebugAccordion data={accordionData} /*debug={debug}*/ />
        </>
    )
}